// Nursly Data Rule: All staff data is fetched exclusively from Supabase.
// Single source of truth: Supabase > staff table + staff_hr (sensitive HR fields).
//
// Sensitive HR fields (DBS, first-aid expiry, L3 qualification details, PFA
// provider) live in the admin-only `staff_hr` table. We transparently merge
// them into the staff records returned by useStaff so existing callers don't
// have to change, non-admin viewers' staff_hr queries simply return [] under
// RLS, so the HR fields stay undefined for them.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { DbStaff, StaffInsert, StaffUpdate } from "@/types/staff";
import { useToast } from "@/hooks/use-toast";
import { useNurseryId } from "@/hooks/useNurseryId";
import { logAudit } from "@/lib/audit";
import { friendlyError } from "@/lib/friendlyError";

const HR_FIELDS = [
  "dbs_certificate_number",
  "dbs_issue_date",
  "dbs_expiry",
  "dbs_type",
  "first_aid_expiry",
  "l3_qualification_type",
  "l3_working_towards_expected_date",
  "pfa_provider",
] as const;
type HrField = (typeof HR_FIELDS)[number];

function splitHr<T extends Record<string, any>>(input: T): { base: Record<string, any>; hr: Record<string, any> } {
  const hr: Record<string, any> = {};
  const base: Record<string, any> = {};
  for (const [k, v] of Object.entries(input)) {
    if ((HR_FIELDS as readonly string[]).includes(k)) hr[k] = v;
    else base[k] = v;
  }
  return { base, hr };
}

export function useStaff(includeInactive = false) {
  const nurseryId = useNurseryId();
  return useQuery({
    queryKey: ["staff", nurseryId, includeInactive],
    enabled: !!nurseryId,
    queryFn: async () => {
      let query = supabase.from("staff").select("*").order("first_name");
      if (!includeInactive) query = query.eq("is_active", true);
      const [staffRes, hrRes] = await Promise.all([
        query,
        supabase.from("staff_hr" as any).select("*"),
      ]);
      if (staffRes.error) throw staffRes.error;
      const hrMap = new Map<string, Record<HrField, any>>(
        (((hrRes.data as any[]) || []).map((r: any) => [r.staff_id, r])),
      );
      return ((staffRes.data as any[]) || []).map((s: any) => ({ ...s, ...(hrMap.get(s.id) || {}) })) as DbStaff[];
    },
  });
}

export function useAddStaff() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const nurseryId = useNurseryId();
  return useMutation({
    mutationFn: async (staff: StaffInsert & Partial<Record<HrField, any>>) => {
      const { base, hr } = splitHr(staff);
      const { data, error } = await supabase
        .from("staff")
        .insert({ ...base, nursery_id: nurseryId } as any)
        .select()
        .single();
      if (error) throw error;
      // Trigger auto-creates an empty staff_hr row. Fill it in if HR data supplied.
      if (Object.keys(hr).length > 0) {
        await supabase
          .from("staff_hr" as any)
          .upsert({ staff_id: (data as any).id, nursery_id: nurseryId, ...hr } as any, { onConflict: "staff_id" });
      }
      return { ...(data as any), ...hr } as DbStaff;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
      toast({ title: "Staff member added ✅" });
      logAudit({ description: `Added staff member: ${data.first_name} ${data.last_name} (${data.level || data.job_title || ""})`, recordType: "staff", recordId: data.id });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to add staff", description: friendlyError(error), variant: "destructive" });
    },
  });
}

export function useUpdateStaff() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: StaffUpdate & Partial<Record<HrField, any>> }) => {
      const { base, hr } = splitHr(updates);
      let row: any;
      if (Object.keys(base).length > 0) {
        const { data, error } = await supabase.from("staff").update(base as any).eq("id", id).select().single();
        if (error) throw error;
        row = data;
      } else {
        const { data, error } = await supabase.from("staff").select("*").eq("id", id).single();
        if (error) throw error;
        row = data;
      }
      if (Object.keys(hr).length > 0) {
        const { error: hrErr } = await supabase
          .from("staff_hr" as any)
          .upsert({ staff_id: id, nursery_id: row.nursery_id, ...hr } as any, { onConflict: "staff_id" });
        if (hrErr) throw hrErr;
      }
      return { ...row, ...hr } as DbStaff;
    },
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
      toast({ title: "Staff updated ✅" });
      const fields = Object.keys(vars.updates || {});
      const isRoleChange = fields.some((f) => f === "level" || f === "job_title");
      const summary = isRoleChange ? "role changed" : `updated ${fields.join(", ") || "fields"}`;
      logAudit({ description: `Edited staff: ${data.first_name} ${data.last_name}: ${summary}`, recordType: "staff", recordId: data.id });
    },
    onError: (error: Error) => {
      toast({ title: "Could not save your changes", description: friendlyError(error), variant: "destructive" });
    },
  });
}

export function useRestoreStaff() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (s: { id: string; first_name: string; last_name: string }) => {
      const { error } = await supabase.rpc("restore_staff" as any, { _staff_id: s.id });
      if (error) throw error;
      return s;
    },
    onSuccess: (s) => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
      toast({ title: `${s.first_name} ${s.last_name} has been restored.`, description: "Add their future shifts and any repeating week again on the Rota." });
      logAudit({ description: `Restored staff member ${s.first_name} ${s.last_name}`, recordType: "staff", recordId: s.id });
    },
    onError: (error: Error) => {
      toast({ title: "Could not restore", description: friendlyError(error), variant: "destructive" });
    },
  });
}

export function useDeactivateStaff() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("staff").update({ is_active: false }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_d, id) => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
      toast({ title: "Staff member deactivated 📦" });
      logAudit({ description: `Archived staff member`, recordType: "staff", recordId: id });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to deactivate", description: friendlyError(error), variant: "destructive" });
    },
  });
}
